import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, throwError } from "rxjs";
import { environment } from '../../../environments/environment';
import { IFeatureFamily, IModifyFeatureFamily } from "../../common/models/feature-family.interface";
import { mapFeatureFamilyRequest, mapFeatureFamilyResponse } from "../../common/models/helpers";

@Injectable({
    providedIn: 'root'
})
export class FeatureFamilyService {
    private httpClient = inject(HttpClient);

    private static readonly endpoints = {
        featureFamilies: `${environment.apiUrlV2}/feature-families`,
    };

    getAll(): Observable<IFeatureFamily[]> {
        const ERROR_MESSAGE = "Failed to get feature families";
        return this.httpClient.get<any[]>(FeatureFamilyService.endpoints.featureFamilies).pipe(
            map((response: any[]) => {
                return response.map(item => mapFeatureFamilyResponse(item));
            }),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    create(family: IModifyFeatureFamily): Observable<IFeatureFamily> {
        const ERROR_MESSAGE = "Failed to create feature family";
        return this.httpClient.post(FeatureFamilyService.endpoints.featureFamilies, mapFeatureFamilyRequest(family)).pipe(
            map((response: any) => {
                return mapFeatureFamilyResponse(response);
            }),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    update(id: string, family: IModifyFeatureFamily): Observable<IFeatureFamily> {
        const ERROR_MESSAGE = "Failed to update feature family";
        return this.httpClient.put(`${FeatureFamilyService.endpoints.featureFamilies}/${id}`, mapFeatureFamilyRequest(family)).pipe(
            map((response: any) => {
                return mapFeatureFamilyResponse(response);
            }),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    delete(id: string): Observable<IFeatureFamily | null> {
        const ERROR_MESSAGE = "Failed to delete feature family";
        return this.httpClient.delete<any | null>(`${FeatureFamilyService.endpoints.featureFamilies}/${id}`).pipe(
            map((response: any | null) => {
                return response ? mapFeatureFamilyResponse(response) : null;
            }),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                const serverMessage = error?.error?.message ?? ERROR_MESSAGE;
                return throwError(() => new Error(serverMessage));
            })
        );
    }
}
